import { useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

// API access for the kitchen view of customer food orders.
export default function useFoodOrderKitchen() {
  const { sendRequest, isLoading, error } = useHttp();
  const authCtx = useContext(AuthContext);

  const authHeaders = useCallback(
    () => ({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + authCtx.token }),
    [authCtx.token]
  );

  // Active orders (in preparation / ready) with the exact order time.
  const getKitchenOverview = useCallback(
    async branchId => {
      return await sendRequest({
        url: '/api/v1/foodOrder/kitchen/overview',
        method: 'GET',
        headers: authHeaders(),
        params: branchId ? { branchId } : {},
      });
    },
    [sendRequest, authHeaders]
  );

  // All locations the caller may see plus the public menu URL their general code
  // encodes and, where tables are configured, the entertainments carrying them.
  // Used by the order QR code view. The backend scopes ':own' callers with
  // assigned branches to those branches.
  const getOrderQrOverview = useCallback(async () => {
    return await sendRequest({
      url: '/api/v1/foodOrder/orderQr/overview',
      method: 'GET',
      headers: authHeaders(),
    });
  }, [sendRequest, authHeaders]);

  // The general order card of a location as PDF: one card whose code carries no
  // table, available for every location.
  const getBranchOrderQrPdf = useCallback(
    async branchId => {
      return await sendRequest({
        url: `/api/v1/foodOrder/orderQr/branch/${branchId}/qr.pdf`,
        method: 'GET',
        headers: authHeaders(),
        returnBlob: true,
      });
    },
    [sendRequest, authHeaders]
  );

  // Printable table cards as PDF. Without a table number every usable table of
  // the entertainment is rendered onto its own page.
  const getTableQrPdf = useCallback(
    async (entertainmentId, tableNumber) => {
      return await sendRequest({
        url: `/api/v1/foodOrder/orderQr/entertainment/${entertainmentId}/qr.pdf`,
        method: 'GET',
        headers: authHeaders(),
        params: tableNumber ? { table: tableNumber } : {},
        returnBlob: true,
      });
    },
    [sendRequest, authHeaders]
  );

  // All table cards of an entertainment as a ZIP with one PDF per table.
  const getTableQrZip = useCallback(
    async entertainmentId => {
      return await sendRequest({
        url: `/api/v1/foodOrder/orderQr/entertainment/${entertainmentId}/qr.zip`,
        method: 'GET',
        headers: authHeaders(),
        returnBlob: true,
      });
    },
    [sendRequest, authHeaders]
  );

  // Advance an order: inPreparation -> ready -> pickedUp, or cancelled. Optional
  // extra flags (e.g. { paymentCollected: true } when handing over a pay-on-site
  // order) are merged into the request body.
  const updateOrderStatus = useCallback(
    async (orderId, status, extra = {}) => {
      return await sendRequest({
        url: `/api/v1/foodOrder/kitchen/${orderId}/status`,
        method: 'PATCH',
        headers: authHeaders(),
        body: { status, ...extra },
      });
    },
    [sendRequest, authHeaders]
  );

  return {
    getKitchenOverview,
    getOrderQrOverview,
    getBranchOrderQrPdf,
    getTableQrPdf,
    getTableQrZip,
    updateOrderStatus,
    isLoading,
    error,
  };
}
